// Global Instructions Rule Applied!
//
// Tranche full-form (mirrors the PowerApps Tranche screen, owner request
// 2026-07-27): tranche facts + current position at the top, then every
// transaction that affected the tranche in chronological order with the
// post-transaction state. Read-only — tranches are engine-owned records
// created at booking; edits happen through asset transactions.
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Card, Spin, Table, Tag } from 'antd'
import dayjs from 'dayjs'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { updateTabTitle } from '../../../store/slices/tabSlice'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { fetchTrancheDetail } from '../services/tranchesService.ts'
import ExportRowsButton from '../../shared/table_grid_export/ExportRowsButton.tsx'
import {
  TrancheDetailPayload,
  TrancheHistoryEntry
} from '../../../state_manager/data_models/tranche_detail_model.ts'

const DATE_FORMAT = 'MM/DD/YYYY'
const DATETIME_FORMAT = 'MM/DD/YYYY HH:mm'

interface FullTrancheFormProps {
  recordId?: string | null
  isNew?: boolean
}

function Row({ label, children }: { label: string; children: React.ReactNode }): React.ReactElement {
  return (
    <div className='flex items-center gap-3 py-1'>
      <span className='w-40 flex-none text-xs text-gray-500'>{label}</span>
      <div className='flex-1 min-w-0 text-sm'>{children}</div>
    </div>
  )
}

function money(symbol: string | null | undefined, value: number | null | undefined): string {
  if (value == null) return '—'
  return `${symbol || ''}${Number(value).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`
}

function count(value: number | null | undefined): string {
  if (value == null) return '—'
  return Number(value).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

function unitValue(symbol: string | null | undefined, value: number | null | undefined): string {
  if (value == null) return '—'
  return `${symbol || ''}${Number(value).toLocaleString('en-US', { maximumFractionDigits: 4 })}`
}

export default function FullTrancheForm({ recordId }: FullTrancheFormProps): React.ReactElement {
  const { openFormInTab } = useTabManagerContext()
  const dispatch = useDispatch()
  const tabs = useSelector(
    (state: { tabs?: { tabs?: Array<{ key: string; module?: string; recordId?: string; title?: string }> } }) =>
      state.tabs?.tabs || []
  )
  const [payload, setPayload] = useState<TrancheDetailPayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Tab titles are slice-derived per module in MainTabContainer; tranches
  // have no slice, so the form names its own tab once the record loads.
  useEffect(() => {
    const name = payload?.tranche?.tranches__name
    if (!name) return
    const tab = tabs.find((t) => t.module === 'tranches' && t.recordId === recordId)
    if (tab && tab.title !== name) {
      dispatch(updateTabTitle({ key: tab.key, title: name }))
    }
  }, [payload, tabs, recordId, dispatch])

  useEffect(() => {
    if (!recordId) return
    let cancelled = false
    ;(async () => {
      setLoading(true)
      const token = getAccessToken(store.getState().auth)
      const result = await fetchTrancheDetail(token, recordId)
      if (!cancelled) {
        setPayload(result.data)
        setError(result.error || null)
        setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [recordId])

  if (loading) {
    return (
      <div className='flex justify-center py-16'>
        <Spin />
      </div>
    )
  }

  if (!payload || error) {
    return (
      <div className='max-w-xl mx-auto text-center py-16 text-gray-500 text-sm'>
        {error || 'Tranche not found.'}
      </div>
    )
  }

  const t = payload.tranche
  const symbol = t.currencies__symbol
  const percExited = t.prog__perc_exited ?? 0
  const exitStatus = percExited >= 1 ? 'Fully Exited' : percExited > 0 ? 'Partial Exit' : 'Active'
  const exitColor = percExited >= 1 ? 'red' : percExited > 0 ? 'gold' : 'green'

  return (
    <div className='h-full overflow-y-auto px-4 py-4'>
      <div className='flex items-center gap-2 mb-4'>
        <h2 className='text-base font-semibold m-0'>{t.tranches__name || 'Tranche'}</h2>
        <Tag color={exitColor}>{exitStatus}</Tag>
        {t.tranches__warehoused && <Tag>Warehoused</Tag>}
        {t.tranches__secondary_purchase && <Tag color='purple'>Secondary purchase</Tag>}
      </div>

      <div className='grid grid-cols-1 xl:grid-cols-2 gap-4'>
        <Card size='small' title='Tranche details'>
          <Row label='Fund'>
            {t.funds__id ? (
              <a onClick={() => openFormInTab('funds', t.funds__id as string, t.funds__name || 'Fund')}>
                {t.funds__name || 'Fund'}
              </a>
            ) : (
              t.funds__name || '—'
            )}
          </Row>
          <Row label='Company'>
            {t.accounts__id ? (
              <a
                onClick={() =>
                  openFormInTab('companies', t.accounts__id as string, t.accounts__name || 'Company')
                }
              >
                {t.accounts__name || 'Company'}
              </a>
            ) : (
              t.accounts__name || '—'
            )}
          </Row>
          <Row label='Security'>{t.securities__name || '—'}</Row>
          <Row label='Security type'>{t.cfg_security_types__label || '—'}</Row>
          <Row label='Valuation type'>{t.cfg_asset_valuation_types__label || '—'}</Row>
          <Row label='Currency'>{t.currencies__code || '—'}</Row>
          <Row label='Investment date'>
            {t.prog__date_invested ? dayjs(t.prog__date_invested).format(DATE_FORMAT) : '—'}
          </Row>
          <Row label='Exit date'>
            {t.prog__date_exited ? dayjs(t.prog__date_exited).format(DATE_FORMAT) : '—'}
          </Row>
          <Row label='Parent tranche'>
            {t.parent_tranches__id ? (
              <a
                onClick={() =>
                  openFormInTab('tranches', t.parent_tranches__id as string, t.parent_tranches__name || 'Tranche')
                }
              >
                {t.parent_tranches__name || 'Tranche'}
              </a>
            ) : (
              '—'
            )}
          </Row>
          <Row label='Ultimate parent'>
            {t.up_tranches__id && t.up_tranches__id !== t.tranches__id ? (
              <a
                onClick={() =>
                  openFormInTab('tranches', t.up_tranches__id as string, t.up_tranches__name || 'Tranche')
                }
              >
                {t.up_tranches__name || 'Tranche'}
              </a>
            ) : (
              t.up_tranches__name || '—'
            )}
          </Row>
          <Row label='Creating transaction'>
            {t.asset_transactions__id ? (
              <a
                onClick={() =>
                  openFormInTab(
                    'asset-transactions',
                    t.asset_transactions__id as string,
                    t.asset_transactions__name || 'Transaction'
                  )
                }
              >
                {t.asset_transactions__name || 'Transaction'}
              </a>
            ) : (
              '—'
            )}
          </Row>
        </Card>

        <Card size='small' title='Position'>
          <Row label='No. held'>{count(t.prog__no_held)}</Row>
          <Row label='Cost held'>{money(symbol, t.prog__cost_held)}</Row>
          {/* The two non-cash projections (asset-delta design, 2026-10-21),
              beside cost held so a cost held above or below the cash paid is
              read for what it is: basis that arrived without cash (a
              fair-value step-up, income added to basis) and the gain or
              loss recognised without proceeds. Shown where either moved. */}
          {t.prog__cost_adjusted || t.prog__gain_recognized ? (
            <>
              <Row label='Cost adjusted'>{money(symbol, t.prog__cost_adjusted)}</Row>
              <Row label='Gain recognised'>{money(symbol, t.prog__gain_recognized)}</Row>
            </>
          ) : null}
          <Row label='Cost (P/S)'>{unitValue(symbol, t.prog__cost_ps)}</Row>
          <Row label='FMV'>{money(symbol, t.prog__fmv)}</Row>
          <Row label='Unit value'>{unitValue(symbol, t.prog__unit_price)}</Row>
          <Row label='Proceeds'>{money(symbol, t.prog__proceeds)}</Row>
          <Row label='Escrow balance'>{money(symbol, t.prog__escrow_balance)}</Row>
          {/* The calculated accrued interest under the security's terms at
              the close of today (at__interest_accrued) - a read of the
              engine, not a booking; shown only where the security carries
              interest terms. */}
          {t.prog__interest_accrued != null ? (
            <Row label='Accrued interest (calc.)'>
              {money(symbol, t.prog__interest_accrued)}
              {t.prog__interest_capitalised ? (
                <span className='text-xs text-gray-500 ml-2'>
                  of which capitalised {money(symbol, t.prog__interest_capitalised)}
                </span>
              ) : null}
            </Row>
          ) : null}
          {/* The score's interest measures (docs/INTEREST_ACCRUAL_DESIGN.md
              section 6.5): the booked balance, the income recognised, the cash
              received as interest, the cost held that came from capitalised
              interest - shown where any of them moved. */}
          {t.prog__interest_balance || t.prog__interest_earned || t.prog__interest_received || t.prog__cost_capitalized ? (
            <>
              <Row label='Accrued interest (booked)'>{money(symbol, t.prog__interest_balance)}</Row>
              <Row label='Interest earned'>{money(symbol, t.prog__interest_earned)}</Row>
              <Row label='Interest received'>{money(symbol, t.prog__interest_received)}</Row>
              <Row label='Capitalised interest'>{money(symbol, t.prog__cost_capitalized)}</Row>
            </>
          ) : null}
          <Row label='% exited'>
            {t.prog__perc_exited != null ? `${(percExited * 100).toFixed(1)}%` : '—'}
          </Row>
        </Card>
      </div>

      <Card
        size='small'
        title='Transaction history'
        className='mt-4'
        extra={
          <ExportRowsButton
            name={`${t.tranches__name || 'Tranche'} Transaction history`}
            rows={payload.history}
            columns={[
              { title: 'Date', value: (r) => r.asset_transactions__transaction_date, kind: 'date' },
              { title: 'Type', value: (r) => r.asset_transaction_types__label },
              { title: 'Transaction', value: (r) => r.asset_transactions__name },
              { title: 'Status', value: (r) => r.transaction_statuses__label },
              { title: 'Cost Held', value: (r) => r.prog__cost_held },
              { title: 'No. Held', value: (r) => r.prog__no_held },
              { title: 'FMV', value: (r) => r.prog__fmv },
              { title: 'FMV (P/S)', value: (r) => r.prog__unit_price },
              { title: 'Unrealized', value: (r) => r.prog__unrealized },
              { title: 'No. Divested', value: (r) => r.prog__no_disposed },
              { title: 'Proceeds', value: (r) => r.prog__proceeds },
              { title: 'Realized', value: (r) => r.prog__realized }
            ]}
          />
        }
      >
        <Table
          size='small'
          rowKey='asset_transactions__id'
          pagination={false}
          dataSource={payload.history}
          scroll={{ x: true }}
          columns={[
            {
              title: 'Date',
              dataIndex: 'asset_transactions__transaction_date',
              render: (v) => (v ? dayjs(String(v)).format(DATETIME_FORMAT) : '—')
            },
            { title: 'Type', dataIndex: 'asset_transaction_types__label', render: (v) => v || '—' },
            {
              title: 'Transaction',
              dataIndex: 'asset_transactions__name',
              render: (v, row: TrancheHistoryEntry) => (
                <a
                  onClick={() =>
                    openFormInTab(
                      'asset-transactions',
                      row.asset_transactions__id,
                      (v as string) || 'Transaction'
                    )
                  }
                >
                  {v || 'Transaction'}
                </a>
              )
            },
            {
              title: 'Status',
              dataIndex: 'transaction_statuses__label',
              render: (v) => (v ? <Tag color={v === 'Booked' ? 'green' : 'blue'}>{v}</Tag> : '—')
            },
            {
              title: 'Cost Held',
              dataIndex: 'prog__cost_held',
              align: 'right' as const,
              render: (v) => money(symbol, v)
            },
            {
              title: 'No. Held',
              dataIndex: 'prog__no_held',
              align: 'right' as const,
              render: (v) => count(v)
            },
            {
              title: 'FMV',
              dataIndex: 'prog__fmv',
              align: 'right' as const,
              render: (v) => money(symbol, v)
            },
            {
              title: 'FMV (P/S)',
              dataIndex: 'prog__unit_price',
              align: 'right' as const,
              render: (v) => unitValue(symbol, v)
            },
            {
              title: 'Unrealized',
              dataIndex: 'prog__unrealized',
              align: 'right' as const,
              render: (v) => money(symbol, v)
            },
            {
              title: 'No. Divested',
              dataIndex: 'prog__no_disposed',
              align: 'right' as const,
              render: (v) => count(v)
            },
            {
              title: 'Proceeds',
              dataIndex: 'prog__proceeds',
              align: 'right' as const,
              render: (v) => money(symbol, v)
            },
            {
              title: 'Realized',
              dataIndex: 'prog__realized',
              align: 'right' as const,
              render: (v) => money(symbol, v)
            }
          ]}
        />
      </Card>
    </div>
  )
}
